export class ValidationInstance {
    constructor(form, validation, options) {
        this._form = form;
        this._validation = validation;
        this._options = Object.assign({
            groupSelector: 'form-group',
            inputSelector: '[name]',
            onValidationError: () => {},
            onValidationSuccess: () => {},
            onClearValidationErrors: () => {},
            onSubmit: () => {},
        }, options ? options : {});
        this.validate = this.validate.bind(this);
        this._validate = this._validate.bind(this);
        this._init = this._init.bind(this);
        this._onSubmit = this._onSubmit.bind(this);
        this.getInput = this.getInput.bind(this);

        this._init();
    }

    get form() {
        return this._form;
    }

    get inputGroups() {
        const selector = this._options.groupSelector ? this._options.groupSelector : 'form-group';
        return Array.from(this._form.querySelectorAll(selector)).filter(group => this.getInput(group) !== null);
    }

    /**
     * @param {HTMLElement} fieldGroup
     * @returns {HTMLInputElement|null}
     */
    getInput(fieldGroup) {
        let input = fieldGroup.querySelector(this._options.inputSelector);
        // checkboxes are rendered with a preceding hidden input to ensure the field is passed (as empty value)
        if (input && input.getAttribute('type') === 'hidden') {
            const sibling = input.nextElementSibling;
            if (sibling && sibling.matches(this._options.inputSelector) && sibling.getAttribute('type') === 'checkbox') {
                input = sibling;
            }
        }
        return input;
    }

    /**
     * @param {HTMLElement} inputGroup
     * @returns {string}
     */
    static getLabel(inputGroup) {
        const explicit = inputGroup.querySelector('[data-validation-label]');
        if (explicit !== null) {
            return explicit.getAttribute('data-validation-label');
        }
        const input = inputGroup.querySelector('input:not([type="hidden"]), select, textarea');
        const type = input !== null ? input.getAttribute('type') : null;
        let source = null;
        // Option groups (radio buttons, multiple checkboxes) carry their question
        // in the legend of their own fieldset, the option labels are not the name.
        if (type === 'radio' || type === 'checkbox') {
            const optionGroup = inputGroup.closest('fieldset.form-group-fieldset')
                || inputGroup.querySelector('fieldset.form-group-fieldset');
            if (optionGroup !== null) {
                source = optionGroup.querySelector('legend');
            }
        }
        if (source === null) {
            const label = inputGroup.querySelector('label');
            if (label !== null) {
                source = label.querySelector('.form-check-label__text') || label;
            }
        }
        if (source === null) {
            // Only a field without any label falls back to the surrounding section legend.
            const fieldset = inputGroup.closest('fieldset');
            source = fieldset !== null ? fieldset.querySelector('legend') : null;
        }
        if (source === null) {
            return '';
        }
        const clone = source.cloneNode(true);
        Array.from(clone.querySelectorAll('.optional')).forEach(node => node.remove());
        return clone.textContent
            .replace(/\s+/g, ' ')
            .replace(/\s*\(optional\)\s*/i, ' ')
            .replace(/\s*\*+\s*$/, '')
            .trim();
    }

    /**
     * @param {HTMLInputElement} input
     * @returns {string}
     */
    static getIdentifier(input) {
        let id = input.id;
        if (input.getAttribute('type') === 'radio') {
            id = id.split('-').slice(0, -1).join('-');
        }
        return id;
    }

    validate(inputGroups) {
        let inputs;
        if (inputGroups) {
            inputs = inputGroups;
        } else {
            inputs = this.inputGroups;
        }
        return Array.from(inputs).reduce((p, group) => this._validate(group)() && p, true);
    }

    _validate(inputGroup) {
        return () => {
            const input = this.getInput(inputGroup);
            if (!input || input.hidden) {
                return;
            }
            let valid = true;
            const validators = this._validation.validators;
            Object.keys(validators).forEach(validatorName => {
                const validator = validators[validatorName];
                if (validator.isApplicable(input, this)) {
                    validator.validate(input, this)(
                        (args) => {
                            valid = false;
                            this._options.onValidationError(inputGroup, validatorName, args);
                        },
                        (args) => {
                            this._options.onValidationSuccess(inputGroup, validatorName, args);
                        });
                }
            });
            return valid;
        };
    }

    _onSubmit(e) {
        e.preventDefault();
        this.clearValidationErrors();
        const result = this.validate();
        this._options.onSubmit(result);
        if (result) {
            this._form.submit();
        }
    }

    _init() {
        Array.from(this.inputGroups).forEach(group => {
            const input = this.getInput(group);
            input.addEventListener('change', this._validate(group));
        });
        this._form.setAttribute('novalidate', '');
        this._form.addEventListener('submit', this._onSubmit);
    }

    clearValidationErrors() {
        this._options.onClearValidationErrors();
    }
}
